import React, { useEffect, useState } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import ApiStatusGraph from './ApiStatusGraph';

/**
 * ApiStatusIndicator - Hamburger Line Loading UI
 * Shows 5 horizontal lines representing API status for the selected version and job.
 * Props:
 *   - selectedVersion: string
 *   - selectedJob: object
 *   - apiStatus: { [key: string]: { summary, requests: [{name, success, time, message}] } }
 */

const ApiStatusIndicator = ({ selectedVersion, selectedJob, apiStatus }) => {
  const [status, setStatus] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedPages, setExpandedPages] = useState({});
  const togglePage = (page) => setExpandedPages(prev => ({ ...prev, [page]: !prev[page] }));

  // Expect apiStatus[`${version}_${job}`] = { summary, requests: [{name, success, time, message}] }
  useEffect(() => {
    if (!selectedVersion || !selectedJob) {
      setStatus(null);
      return;
    }
    // selectedJob is the job details object, we need the BU_ID for the key
    const jobId = selectedJob.BU_ID || selectedJob.Job_GUID || selectedJob;
    const key = `${selectedVersion}_${jobId}`;
    //console.log('ApiStatusIndicator key:', key, 'Available keys:', Object.keys(apiStatus));
    const currentStatus = apiStatus?.[key] || null;
    setStatus(currentStatus);
    
    // Check if any APIs are still loading (requested but no response yet)
    const hasRequests = currentStatus?.requests && currentStatus.requests.length > 0;
    const allComplete = hasRequests && currentStatus.requests.length >= 4;
    setIsLoading(!allComplete);
  }, [selectedVersion, selectedJob, apiStatus]);

  if (!selectedVersion || !selectedJob) return null;

  const togglePopup = () => {
    console.log('Toggling API status popup');
    setShowPopup(prev => !prev);
  };

  // Define the 4 APIs to track (core set, always shown at top)
  const apis = [
    { name: 'loadUserDataCombined', label: 'User Data' },
    { name: 'loadJobBillingsCombined', label: 'Billings' },
    { name: 'loadJobOpenCommitmentsCombined', label: 'Open Committments' },
    { name: 'loadVariance', label: 'Hour WIP Variance' }
  ];

  // Find core-page version of a request (per-page entries are tagged page='bidForecast' etc.)
  const findCoreRequest = (apiName) => {
    if (!status?.requests) return null;
    return status.requests.find(r => r.name === apiName && (!r.page || r.page === 'core')) || null;
  };

  const getStatus = (apiName) => {
    const request = findCoreRequest(apiName);
    if (!request) return 'pending';
    return request.success ? 'success' : 'error';
  };

  const getColor = (apiStatus) => {
    switch (apiStatus) {
      case 'success': return '#4CAF50';
      case 'error': return '#F44336';
      case 'pending': return '#E0E0E0';
      default: return '#E0E0E0';
    }
  };

  const getTime = (apiName) => {
    const request = findCoreRequest(apiName);
    return request?.time;
  };

  // Group non-core requests by page for the per-page panel.
  const pageGroups = React.useMemo(() => {
    if (!status?.requests) return [];
    const byPage = {};
    status.requests.forEach(r => {
      const page = r.page || 'core';
      if (page === 'core') return;
      if (!byPage[page]) byPage[page] = [];
      byPage[page].push(r);
    });
    return Object.entries(byPage)
      .map(([page, reqs]) => {
        const lastTs = Math.max(...reqs.map(r => r.ts || 0));
        const failures = reqs.filter(r => !r.success);
        const lastSave = reqs
          .filter(r => r.kind === 'save')
          .sort((a, b) => (b.ts || 0) - (a.ts || 0))[0];
        return {
          page,
          reqs: reqs.sort((a, b) => (b.ts || 0) - (a.ts || 0)),
          lastTs,
          failures,
          lastSave,
        };
      })
      .sort((a, b) => b.lastTs - a.lastTs);
  }, [status]);

  const fmtTime = (ts) => {
    if (!ts) return '';
    const ageSec = Math.round((Date.now() - ts) / 1000);
    if (ageSec < 60) return `${ageSec}s ago`;
    if (ageSec < 3600) return `${Math.round(ageSec / 60)}m ago`;
    return `${Math.round(ageSec / 3600)}h ago`;
  };

  // Determine overall status
  const hasError = status?.requests?.some(r => !r.success);
  const circleColor = hasError ? '#F44336' : isLoading ? '#E0E0E0' : '#4CAF50';

  return (
    <Pressable
      style={styles.container}
      onPress={togglePopup}
    >
      {/* Circle Indicator */}
      <View style={styles.circleContainer}>
        <View 
          style={[
            styles.circle,
            { 
              borderColor: circleColor,
              position: 'relative',
              overflow: 'hidden'
            }
          ]}
        >
          {isLoading && (
            <View style={styles.loadingPulse} />
          )}
        </View>
      </View>

      {/* Detailed Popup - Click to toggle */}
      {showPopup && (
        <View style={styles.popup} onClick={(e) => e.stopPropagation()}>
          <View style={styles.popupHeader}>
            <View>
              <Text style={styles.popupTitle}>API Status</Text>
              <Text style={styles.popupSubtitle}>
                Job: {selectedJob.BU_ID} | Version: {selectedVersion}
              </Text>
            </View>
            <Pressable 
              onPress={(e) => {
                e.stopPropagation();
                togglePopup();
              }} 
              style={styles.closeButton}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
          </View>
          {/* {status?.requests && status.requests.length > 0 ? (
            <ApiStatusGraph requests={status.requests} />
          ) : (
            <Text style={styles.popupEmpty}>Loading...</Text>
          )} */}
          {apis.map((api) => {
            const apiStatus = getStatus(api.name);
            const time = getTime(api.name);
            const icon = apiStatus === 'success' ? '✓' : apiStatus === 'error' ? '✗' : '○';
            const isApiLoading = apiStatus === 'pending' && isLoading;
            return (
              <View key={api.name} style={styles.popupRow}>
                <Text style={{ fontSize: 14, color: getColor(apiStatus), marginRight: 4, fontWeight: 'bold' }}>
                  {isApiLoading ? '⟳' : icon}
                </Text>
                <Text style={{ fontSize: 12, color: getColor(apiStatus), marginRight: 6, minWidth: 120 }}>
                  {api.label}
                </Text>
                {time ? (
                  <Text style={{ fontSize: 12, color: '#333' }}>{time}ms</Text>
                ) : isApiLoading ? (
                  <Text style={{ fontSize: 12, color: '#999' }}>Loading...</Text>
                ) : null}
              </View>
            );
          })}

          {pageGroups.length > 0 && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionLabel}>Per-Page Activity</Text>
              {pageGroups.map(group => {
                const pageColor = group.failures.length ? '#F44336' : '#4CAF50';
                const isOpen = !!expandedPages[group.page];
                return (
                  <View key={group.page} style={{ marginBottom: 4 }}>
                    <Pressable
                      onPress={(e) => { e.stopPropagation(); togglePage(group.page); }}
                      style={styles.popupRow}
                    >
                      <Text style={{ fontSize: 14, color: pageColor, marginRight: 4, fontWeight: 'bold' }}>
                        {group.failures.length ? '✗' : '✓'}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#222', marginRight: 6, minWidth: 140, fontWeight: 'bold' }}>
                        {group.page}
                      </Text>
                      <Text style={{ fontSize: 11, color: '#666', marginRight: 6 }}>
                        {group.reqs.length} call{group.reqs.length === 1 ? '' : 's'}
                        {group.failures.length ? ` · ${group.failures.length} failed` : ''}
                      </Text>
                      <Text style={{ fontSize: 11, color: '#999' }}>
                        {fmtTime(group.lastTs)}
                      </Text>
                      <Text style={{ fontSize: 11, color: '#999', marginLeft: 6 }}>{isOpen ? '▾' : '▸'}</Text>
                    </Pressable>
                    {group.lastSave && !isOpen && (
                      <Text style={{ fontSize: 10, color: group.lastSave.success ? '#4CAF50' : '#F44336', marginLeft: 22 }}>
                        last save: {group.lastSave.name} · {group.lastSave.success ? 'ok' : 'FAILED'} · {fmtTime(group.lastSave.ts)}
                      </Text>
                    )}
                    {isOpen && group.reqs.map((r, idx) => (
                      <View key={r.name + '_' + idx} style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 22, marginBottom: 2 }}>
                        <Text style={{ fontSize: 11, color: r.success ? '#4CAF50' : '#F44336', marginRight: 4, fontWeight: 'bold' }}>
                          {r.success ? '✓' : '✗'}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#666', marginRight: 4, minWidth: 38 }}>
                          {r.kind || 'load'}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#222', marginRight: 6, minWidth: 180 }}>
                          {r.name}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#666', marginRight: 6 }}>
                          {r.time != null ? `${r.time}ms` : ''}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#999' }}>
                          {fmtTime(r.ts)}
                        </Text>
                        {r.message && !r.success && (
                          <Text style={{ fontSize: 10, color: '#F44336', marginLeft: 6 }}>
                            {r.message}
                          </Text>
                        )}
                      </View>
                    ))}
                  </View>
                );
              })}
            </>
          )}
        </View>
      )}
    </Pressable>
  );
};

const styles = {
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 8,
    cursor: 'pointer',
  },
  circleContainer: {
    padding: 4,
  },
  circle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 3,
    borderStyle: 'solid',
    position: 'relative',
  },
  loadingPulse: {
    position: 'absolute',
    top: -3,
    left: -3,
    right: -3,
    bottom: -3,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#999',
    opacity: 0.3,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  popup: {
    position: 'absolute',
    top: 28,
    right: 0,
    zIndex: 100,
    backgroundColor: '#fff',
    borderColor: '#bbb',
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    minWidth: 460,
    maxWidth: 620,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  divider: {
    height: 1,
    backgroundColor: '#eee',
    marginVertical: 8,
  },
  sectionLabel: {
    fontSize: 11,
    color: '#666',
    fontWeight: 'bold',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  popupHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  popupTitle: {
    fontWeight: 'bold',
    fontSize: 14,
    color: '#222',
  },
  popupSubtitle: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
  closeButton: {
    padding: 4,
    cursor: 'pointer',
  },
  closeButtonText: {
    fontSize: 16,
    color: '#999',
    fontWeight: 'bold',
  },
  popupRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  popupEmpty: {
    fontSize: 12,
    color: '#888',
    marginBottom: 8,
  },
};

export default ApiStatusIndicator;


